// One line under the preview saying how the product photos in its newest answer look: whole and
// sharp, or what is wrong, with "Fix with Chat", which drafts the request with the facts attached.
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import storefrontCopy, { type StorefrontCopyKey } from '@/i18n/ns/storefront';
import { WorkspaceIcon } from '../icons';
import { photosFixable, photosLookRight, summarizePhotos, type PhotoFact } from './photos';

type Copy = Readonly<Record<StorefrontCopyKey, string>>;

export function PhotoCheck({
  photos,
  onFix,
}: {
  photos: PhotoFact[];
  onFix: (facts: string) => void;
}) {
  const copy = useNamespaceCopy(storefrontCopy);
  const summary = summarizePhotos(photos);
  const good = photosLookRight(summary);
  const issues = [
    summary.small ? formatCopy(copy.photosSmall, { count: String(summary.small) }) : null,
    summary.cut ? formatCopy(copy.photosCut, { count: String(summary.cut) }) : null,
    summary.soft ? formatCopy(copy.photosSoft, { count: String(summary.soft) }) : null,
    summary.missing ? formatCopy(copy.photosMissing, { count: String(summary.missing) }) : null,
  ].filter(Boolean);
  return (
    <p
      className="sf-photo-check"
      data-state={good ? 'good' : 'issue'}
      data-testid="sf-photo-check"
      role="status"
    >
      <WorkspaceIcon name={good ? 'check' : 'alert'} size={14} />
      {good ? (
        <span>
          {summary.total === 1
            ? copy.photosGoodOne
            : formatCopy(copy.photosGood, { count: String(summary.total) })}
        </span>
      ) : (
        <>
          <b>{copy.photosLabel}</b>
          <span>{issues.join(' · ')}</span>
          {photosFixable(summary) && (
            <button
              type="button"
              className="sf-photo-fix"
              data-testid="sf-photo-fix"
              onClick={() => onFix(photoFactsText(photos, copy))}
            >
              {copy.photosFix}
            </button>
          )}
        </>
      )}
    </p>
  );
}

/** The facts Chat gets with the request, in at most 160 characters (the selection's limit). */
export function photoFactsText(photos: PhotoFact[], copy: Copy): string {
  const summary = summarizePhotos(photos);
  const sizes = photos
    .filter((photo) => photo.size)
    .map((photo) => `${photo.size![0]}×${photo.size![1]}`)
    .join(', ');
  const text = formatCopy(copy.photosFacts, {
    total: String(summary.total),
    small: String(summary.small),
    cut: String(summary.cut),
    soft: String(summary.soft),
    missing: String(summary.missing),
    sizes,
  });
  return text.length > 160 ? `${text.slice(0, 159)}…` : text;
}
